import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import Document from '../icon/document';
import Link from '../link/link';
import TruncatedText from '../truncated-text';
import Typography from '../typography/typography';

export interface IViewDocument {
  file: string | File;
  name: string;
  description?: string;
}

export interface IFileViewList {
  documents: IViewDocument[];
}

export function FileViewList({ documents }: IFileViewList) {
  const theme = useTheme();

  const getHref = (fileUrl: string | File) => {
    if (fileUrl) {
      if (typeof fileUrl === 'string') {
        return fileUrl;
      } else {
        const file = fileUrl;
        const blob = new Blob([file], { type: file.type });
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
      }
    }
    return '';
  };

  return (
    <StyledDocumentList>
      {documents.map((document, index) => (
        <ListItem key={`${document.name}-${index}`}>
          <DocumentHeaderContainer key={document.name}>
            <DocumentContainer>
              <Document />
            </DocumentContainer>
            {/* Treat Documents as external links, so that they open as a new tab, rather than redirect */}
            <Link
              color={theme.palette.primary.main}
              href={getHref(document.file)}
              data-testid="document-title"
              isExternalLink
              isDownload
              isTruncated
              label={document.name}
            >
              <TruncatedText lineClamp={1}>{document.name}</TruncatedText>
            </Link>
          </DocumentHeaderContainer>
          <DocumentDescription variant={TypographyVariant.bodyMd1}>
            {document.description}
          </DocumentDescription>
        </ListItem>
      ))}
    </StyledDocumentList>
  );
}

export default memo(FileViewList);

const StyledDocumentList = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing(3)};
  `}
`;
const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  vertical-align: middle;
`;
const DocumentHeaderContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: flex-start;
    align-items: baseline;
    vertical-align: middle;
    text-align: center;
    color: ${theme.palette.neutral[100]};
  `}
`;

const DocumentDescription = styled(Typography)`
  ${({ theme }) => css`
    margin-left: ${theme.spacing(2.5)};
    text-align: left;
    overflow-wrap: anywhere;
    margin-top: ${theme.spacing(1)};
  `}
`;

const DocumentContainer = styled.div`
  ${({ theme }) => css`
    margin-right: ${theme.spacing(0.5)};
    line-height: 16px;

    svg {
      margin-bottom: -1.5px;
    }
  `}
`;
